import React from "react";
import { Button } from "./ui/button";
import { 
  Shield, 
  MapPin, 
  Clock, 
  Bell, 
  Database, 
  Smartphone,
  Route,
  AlertTriangle
} from "lucide-react";

import { mockData } from "../mock";

const SecuritySection = () => {
  const { links } = mockData.company;
  const features = [
    {
      icon: MapPin,
      title: "Localização em Tempo Real",
      description: "Acompanhe a posição exata do seu veículo a qualquer momento, com atualizações a cada segundo."
    },
    {
      icon: Route,
      title: "Histórico de Rotas",
      description: "Visualize todas as rotas percorridas com horários detalhados e quilometragem."
    },
    {
      icon: Bell,
      title: "Alertas e Notificações",
      description: "Receba notificações instantâneas sobre movimentações, velocidade e áreas restritas."
    },
    {
      icon: Database,
      title: "Backup em Nuvem",
      description: "Todos os dados são armazenados com segurança em servidores protegidos."
    }
  ];

  return (
    <section className="py-24 px-4 relative">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-orange-500/5 to-transparent"></div>
      
      <div className="container mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left side - App mockup */}
          <div className="flex justify-center lg:order-2">
            <div className="relative">
              {/* Main phone */}
              <div className="w-72 h-96 bg-gradient-to-b from-gray-800 to-gray-900 rounded-3xl p-5 shadow-2xl shadow-orange-500/20 border border-gray-700">
                <div className="w-full h-full bg-black rounded-2xl p-4">
                  {/* Status bar */}
                  <div className="flex justify-between items-center text-white text-sm mb-4">
                    <span>9:41</span>
                    <div className="flex items-center gap-1">
                      <Bell className="w-4 h-4 text-yellow-400" />
                      <span>4G</span>
                    </div>
                  </div>
                  
                  {/* App header */}
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-yellow-400 font-bold text-lg">Rastreamento</h3>
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  </div>
                  
                  {/* Map area */}
                  <div className="bg-gradient-to-br from-blue-900/40 to-green-900/40 rounded-lg p-4 mb-4 relative h-48">
                    <div className="absolute top-4 left-4 flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                      <span className="text-xs text-gray-300">Online</span>
                    </div>
                    
                    {/* Route line simulation */}
                    <svg className="absolute inset-0 w-full h-full">
                      <path
                        d="M20 40 Q60 20 120 60 T180 40"
                        stroke="#FFD700"
                        strokeWidth="2"
                        fill="none"
                        strokeDasharray="5,5"
                        className="animate-pulse"
                      />
                    </svg>
                    
                    <div className="absolute bottom-4 right-4">
                      <MapPin className="w-6 h-6 text-orange-500" />
                    </div>
                  </div>
                  
                  {/* Vehicle status */}
                  <div className="space-y-2">
                    <div className="bg-gray-800 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-white font-semibold">Honda Civic</span>
                        <span className="text-green-400 text-sm">Parado</span>
                      </div>
                      <p className="text-gray-400 text-xs">Rua das Flores, 123</p>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="bg-gray-800 rounded p-2 text-center">
                        <Clock className="w-4 h-4 text-yellow-400 mx-auto mb-1" />
                        <span className="text-gray-300">15:30</span>
                      </div>
                      <div className="bg-gray-800 rounded p-2 text-center">
                        <Route className="w-4 h-4 text-orange-500 mx-auto mb-1" />
                        <span className="text-gray-300">45 km</span>
                      </div>
                      <div className="bg-gray-800 rounded p-2 text-center">
                        <AlertTriangle className="w-4 h-4 text-red-500 mx-auto mb-1" />
                        <span className="text-gray-300">0</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating notification */}
              <div className="absolute -top-4 -right-6 bg-red-500 text-white text-xs px-3 py-2 rounded-full shadow-lg animate-bounce">
                Alerta!
              </div>
              
              {/* Floating GPS signal */}
              <div className="absolute -bottom-4 -left-6 bg-green-500 text-white text-xs px-3 py-2 rounded-full shadow-lg flex items-center gap-1">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                GPS
              </div>
            </div>
          </div>
          
          {/* Right side - Content */}
          <div className="space-y-8 lg:order-1">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                  Tecnologia de Ponta
                </span> em Rastreamento
              </h2>
              <p className="text-xl text-gray-300 leading-relaxed">
                Sistema completo de monitoramento veicular com recursos avançados 
                para máxima segurança e controle.
              </p>
            </div>
            
            {/* Features grid */}
            <div className="grid gap-6">
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div 
                    key={index}
                    className="flex gap-4 p-4 rounded-lg bg-gray-900/30 border border-gray-800 hover:border-yellow-500/30 transition-all duration-300 hover:bg-gray-900/50"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-black" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-gray-400">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-semibold hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 shadow-lg shadow-yellow-500/25"
                asChild
              >
                <a href={links.whatsapp} target="_blank" rel="noopener noreferrer">
                  Começar Teste Gratuito
                </a>
              </Button>
              
              <Button 
                variant="outline" 
                size="lg"
                className="border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black transition-all duration-300"
                asChild
              >
                <a href={links.demo} target="_blank" rel="noopener noreferrer">
                  <Smartphone className="w-5 h-5 mr-2" />
                  Ver Demo do App
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SecuritySection;